import { defineStore } from 'pinia';
import i18n, { SUPPORTED_LOCALES, type SupportedLocale } from '@/i18n';
import { authDb, type AuthUser, type SessionRow } from '@/db/auth';
import {
  webAuthnCreateCredential,
  webAuthnErrorMessage,
  webAuthnGetAssertion,
} from '@/utils/webauthn';

export type AuthStatus = 'uninitialized' | 'loading' | 'ready' | 'error';

/** Normalized signup validation errors from the API. */
export interface SignupErrors {
  fieldErrors: Record<string, string[]>;
  generalError: string | null;
}

/** django-allauth headless GET session response shape. */
interface SessionResponse {
  status?: number;
  data?: { user?: AuthUser; methods?: unknown };
  meta?: { is_authenticated?: boolean; session_token?: string };
  errors?: unknown;
}

const API_BASE = '';
// Use browser client so the existing sessionid cookie (e.g. from Wagtail admin) is used.
// The app client only authenticates via X-Session-Token and ignores cookies.
const SESSION_URL = `${API_BASE}/_allauth/browser/v1/auth/session`;
const SIGNUP_URL = `${API_BASE}/_allauth/browser/v1/auth/signup`;
const VERIFY_EMAIL_URL = `${API_BASE}/_allauth/browser/v1/auth/email/verify`;
const RESEND_VERIFY_EMAIL_URL = `${API_BASE}/_allauth/browser/v1/auth/email/resend`;
const LOGIN_URL = `${API_BASE}/_allauth/browser/v1/auth/login`;
const PASSWORD_RESET_REQUEST_URL = `${API_BASE}/_allauth/browser/v1/auth/password/request`;
const PASSWORD_RESET_URL = `${API_BASE}/_allauth/browser/v1/auth/password/reset`;
const LOGIN_CODE_REQUEST_URL = `${API_BASE}/_allauth/browser/v1/auth/code/request`;
const LOGIN_CODE_CONFIRM_URL = `${API_BASE}/_allauth/browser/v1/auth/code/confirm`;
const CHANGE_PASSWORD_URL = `${API_BASE}/_allauth/browser/v1/account/password/change`;
const PASSKEY_LOGIN_URL = `${API_BASE}/_allauth/browser/v1/auth/webauthn/login`;
/** Completes WebAuthn after email/password when MFA stage `mfa_authenticate` is pending (not passwordless login). */
const MFA_WEBAUTHN_AUTHENTICATE_URL = `${API_BASE}/_allauth/browser/v1/auth/webauthn/authenticate`;
const WEBAUTHN_MANAGE_URL = `${API_BASE}/_allauth/browser/v1/account/authenticators/webauthn`;
const AUTHENTICATORS_LIST_URL = `${API_BASE}/_allauth/browser/v1/account/authenticators`;
const REAUTHENTICATE_URL = `${API_BASE}/_allauth/browser/v1/auth/reauthenticate`;
const REAUTHENTICATE_CODE_REQUEST_URL = `${API_BASE}/_allauth/browser/v1/auth/reauthenticate/code/request`;
const REAUTHENTICATE_CODE_CONFIRM_URL = `${API_BASE}/_allauth/browser/v1/auth/reauthenticate/code/confirm`;

/** After password login, headless returns 401 with `mfa_authenticate` pending (second factor). */
function parseLoginMfaPending(body: unknown): 'webauthn' | 'other' | null {
  if (body === null || typeof body !== 'object') return null;
  const flows = (body as { data?: { flows?: unknown } }).data?.flows;
  if (!Array.isArray(flows)) return null;
  const pending = flows.find(
    (f) =>
      f &&
      typeof f === 'object' &&
      (f as { id?: string }).id === 'mfa_authenticate' &&
      (f as { is_pending?: boolean }).is_pending === true
  ) as { types?: string[] } | undefined;
  if (!pending) return null;
  const types = Array.isArray(pending.types) ? pending.types : [];
  if (types.includes('webauthn')) return 'webauthn';
  return 'other';
}

/** django-allauth: sensitive action requires password again (session still valid). */
function responseRequiresReauthenticate(body: unknown): boolean {
  if (body === null || typeof body !== 'object') return false;
  const data = (body as { data?: { flows?: Array<{ id?: string }> } }).data;
  const flows = data?.flows;
  if (!Array.isArray(flows)) return false;
  return flows.some((f) => f?.id === 'reauthenticate');
}

type PasskeyRegistrationCoreResult =
  | { kind: 'ok' }
  | { kind: 'reauth' }
  | { kind: 'error'; message: string };

type PasskeyRegisterErrorI18nKey =
  | 'account.dashboard.passkeyReauthSecurityMessage'
  | 'account.dashboard.passkeyReauthSessionAgainMessage';

type PasskeyReauthPasswordErrorI18nKey = 'account.dashboard.passkeyReauthIncorrectPassword';

function passkeyT(
  key:
    | 'account.dashboard.passkeyReauthSecurityMessage'
    | 'account.dashboard.passkeyReauthSessionAgainMessage'
    | 'account.dashboard.passkeyReauthIncorrectPassword'
    | 'account.dashboard.passkeyReauthPasswordConfirmFailed'
    | 'account.dashboard.passkeyReauthSignInAgain'
    | 'account.dashboard.passkeyReauthSendCodeFailed'
    | 'account.dashboard.passkeyReauthVerifyFailed'
    | 'account.dashboard.passkeyReauthGenericError',
  values?: Record<string, string | number>
): string {
  return String(i18n.global.t(key, values ?? {}));
}

const SESSION_ROW_ID = 'default' as const;

/** Django CSRF cookie name. */
const CSRF_COOKIE_NAME = 'csrftoken';

function getCookie(name: string): string | null {
  if (!name) return null;
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  for (const cookie of cookies) {
    const [key, ...rest] = cookie.split('=');
    if (key === name) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return null;
}

/** `Accept-Language` for Django `LocaleMiddleware`; matches active vue-i18n locale. */
function acceptLanguageForDjango(): SupportedLocale {
  const raw = i18n.global.locale.value;
  const s = (typeof raw === 'string' ? raw.trim() : String(raw ?? '').trim()).toLowerCase();
  return (SUPPORTED_LOCALES as readonly string[]).includes(s) ? (s as SupportedLocale) : 'tet';
}

/** Merge into headless `_allauth` fetch headers so transactional email matches SPA language. */
function withDjangoLocaleHeaders(headers: HeadersInit): HeadersInit {
  return {
    ...(headers as Record<string, string>),
    'Accept-Language': acceptLanguageForDjango(),
  };
}

/** Strip HTTP status codes from API messages so the UI never shows "(401)" or similar. */
function sanitizeAuthErrorMessage(message: string | null | undefined): string {
  const raw = (message ?? '').trim();
  if (!raw) return '';
  return raw
    .replace(/\s*\(\d{3}\)\s*/g, ' ')
    .replace(/\bHTTP\s+1\.\d+\s+\d{3}\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    status: 'uninitialized' as AuthStatus,
    error: null as string | null,
    user: null as AuthUser | null,
    sessionToken: null as string | null,
    isAuthenticated: false,
    signupLoading: false,
    signupErrors: {
      fieldErrors: {} as Record<string, string[]>,
      generalError: null as string | null,
    },
    signupSuccess: false,
    loginLoading: false,
    loginErrors: {
      fieldErrors: {} as Record<string, string[]>,
      generalError: null as string | null,
    },
    loginCodeSent: false,
    loginCodeSentTo: null as string | null,
    loginCodeConfirmError: null as string | null,
    /** After password login: MFA step required (`mfa_authenticate` in headless `data.flows`). */
    loginPendingMfa: null as null | 'webauthn' | 'other',
    passwordChangeLoading: false,
    passwordChangeErrors: {
      fieldErrors: {} as Record<string, string[]>,
      generalError: null as string | null,
    },
    passwordChangeSuccess: false,
    passkeyRegisterLoading: false,
    passkeyRenameLoading: false,
    passkeyRegisterError: null as string | null,
    /** When set, the passkeys banner uses vue-i18n in the component (follows locale changes). */
    passkeyRegisterErrorI18nKey: null as PasskeyRegisterErrorI18nKey | null,
    /** True after GET/POST passkey registration returns 401 with flows.reauthenticate. */
    passkeyNeedsReauthenticate: false,
    passkeyReauthPasswordError: null as string | null,
    passkeyReauthPasswordErrorI18nKey: null as PasskeyReauthPasswordErrorI18nKey | null,
    passkeyReauthCodeLoading: false,
    passkeyReauthCodeSent: false,
    passkeyReauthCodeSentTo: null as string | null,
    passkeyReauthCodeError: null as string | null,
    /** Stashed register options while waiting for password reconfirmation. */
    passkeyPendingRegisterOptions: null as { name?: string } | null,
    /** WebAuthn passkeys for the signed-in user (empty until loadPasskeys runs). */
    passkeys: [] as Array<{ id: number; name: string }>,
  }),

  getters: {
    /** Current user; null when not authenticated. */
    currentUser: (state): AuthUser | null => state.user,
    /** Whether the session has been loaded (from network or cache). */
    isReady: (state): boolean => state.status === 'ready' || state.status === 'error',
  },

  actions: {
    /**
     * Load session: use cached user when available (offline/fast bootstrap), then
     * fetch GET session from the headless API and update state + IndexedDB.
     * Call on app init so user details are available to all components.
     */
    async initialize(): Promise<void> {
      this.status = 'loading';
      this.error = null;

      let cached: SessionRow | undefined;
      try {
        cached = await authDb.session.get(SESSION_ROW_ID);
        if (cached) {
          this.user = cached.user;
          this.sessionToken = cached.sessionToken;
          this.isAuthenticated = cached.isAuthenticated;
          this.status = 'ready';
        }
      } catch {
        // IndexedDB unavailable (e.g. private browsing)
      }

      const headers = withDjangoLocaleHeaders({ Accept: 'application/json' }) as Record<
        string,
        string
      >;
      if (cached?.sessionToken) {
        headers['X-Session-Token'] = cached.sessionToken;
      }

      try {
        const response = await fetch(SESSION_URL, {
          credentials: 'same-origin',
          headers,
        });
        let body: SessionResponse;
        try {
          body = (await response.json()) as SessionResponse;
        } catch {
          body = {};
        }
        const meta = body.meta ?? {};
        const data = body.data ?? {};
        const isAuthenticated =
          response.ok && Boolean(meta.is_authenticated && data.user);
        const user = isAuthenticated ? (data.user as AuthUser) : null;
        const sessionToken =
          typeof meta.session_token === 'string'
            ? meta.session_token
            : this.sessionToken;

        this.user = user;
        this.sessionToken = sessionToken;
        this.isAuthenticated = isAuthenticated;
        this.status = 'ready';

        try {
          await authDb.session.put({
            id: SESSION_ROW_ID,
            user,
            sessionToken,
            isAuthenticated,
            fetchedAt: Date.now(),
          });
        } catch {
          // Ignore cache write failures
        }
      } catch (e) {
        this.status = 'error';
        this.error = e instanceof Error ? e.message : String(e);
        // Keep cached state if we had it (offline)
        if (!cached) {
          this.user = null;
          this.sessionToken = null;
          this.isAuthenticated = false;
        } else {
          this.status = 'ready';
        }
      }
    },

    /**
     * Update stored session token (e.g. after signup/confirm/login).
     * Call with the meta.session_token from the auth API response.
     */
    setSessionToken(token: string | null): void {
      this.sessionToken = token;
      if (token) {
        void this.persistSession();
      } else {
        void authDb.session.put({
          id: SESSION_ROW_ID,
          user: this.user,
          sessionToken: null,
          isAuthenticated: this.isAuthenticated,
          fetchedAt: Date.now(),
        });
      }
    },

    /**
     * Persist current state to IndexedDB (e.g. after login/signup/confirm).
     */
    async persistSession(): Promise<void> {
      try {
        await authDb.session.put({
          id: SESSION_ROW_ID,
          user: this.user,
          sessionToken: this.sessionToken,
          isAuthenticated: this.isAuthenticated,
          fetchedAt: Date.now(),
        });
      } catch {
        // Ignore
      }
    },

    /**
     * Update store from an auth API response (e.g. after email confirm or login).
     * Use when you have a response with data.user and meta.session_token.
     */
    setSessionFromResponse(data: {
      user?: AuthUser | null;
      session_token?: string | null;
    }): void {
      const user = data.user ?? null;
      const sessionToken =
        typeof data.session_token === 'string' ? data.session_token : this.sessionToken;
      this.user = user;
      this.sessionToken = sessionToken;
      this.isAuthenticated = Boolean(user);
      this.status = 'ready';
      void this.persistSession();
    },

    /**
     * Clear session (e.g. after logout). Call DELETE session on the API first if needed.
     */
    clearSession(): void {
      this.user = null;
      this.sessionToken = null;
      this.isAuthenticated = false;
      this.passkeys = [];
      this.passkeyRegisterError = null;
      this.passkeyRegisterErrorI18nKey = null;
      this.passkeyRenameLoading = false;
      this.passkeyNeedsReauthenticate = false;
      this.passkeyReauthPasswordError = null;
      this.passkeyReauthPasswordErrorI18nKey = null;
      this.passkeyReauthCodeLoading = false;
      this.passkeyReauthCodeSent = false;
      this.passkeyReauthCodeSentTo = null;
      this.passkeyReauthCodeError = null;
      this.passkeyPendingRegisterOptions = null;
      this.status = 'ready';
      void authDb.session.put({
        id: SESSION_ROW_ID,
        user: null,
        sessionToken: null,
        isAuthenticated: false,
        fetchedAt: Date.now(),
      });
    },

    /**
     * Log out: invalidate session on the server (cookie and/or token), then clear local state.
     * Always call DELETE so the sessionid cookie is invalidated; with browser client the
     * server often does not return session_token, so sessionToken can be null after refresh.
     * No redirect; the caller (e.g. account page) may redirect after await.
     */
    async logout(): Promise<void> {
      const headers = withDjangoLocaleHeaders({ Accept: 'application/json' }) as Record<
        string,
        string
      >;
      const csrfToken = getCookie(CSRF_COOKIE_NAME);
      if (csrfToken) headers['X-CSRFToken'] = csrfToken;
      if (this.sessionToken) {
        headers['X-Session-Token'] = this.sessionToken;
      }
      try {
        await fetch(SESSION_URL, {
          method: 'DELETE',
          credentials: 'same-origin',
          headers,
        });
      } catch {
        // Proceed to clear local state even if the request fails
      }
      this.clearSession();
    },

    /**
     * Revalidate session from the API (e.g. on focus or after returning to app).
     */
    async revalidate(): Promise<void> {
      if (this.status !== 'ready' && this.status !== 'error') return;
      this.status = 'loading';
      this.error = null;
      await this.initialize();
    },

    /**
     * Clear signup UI state (errors and success message). Call when showing the form again.
     */
    clearSignupState(): void {
      this.signupErrors = {
        fieldErrors: {},
        generalError: null,
      };
      this.signupSuccess = false;
    },

    /**
     * Clear login UI state (errors, code-sent). Call when switching to sign up or leaving account.
     */
    clearLoginState(): void {
      this.loginLoading = false;
      this.loginErrors = { fieldErrors: {}, generalError: null };
      this.loginCodeSent = false;
      this.loginCodeSentTo = null;
      this.loginCodeConfirmError = null;
      this.loginPendingMfa = null;
    },

    /**
     * Clear password-change UI state (errors, success). Call when closing the form or retrying.
     */
    clearPasswordChangeState(): void {
      this.passwordChangeErrors = { fieldErrors: {}, generalError: null };
      this.passwordChangeSuccess = false;
    },

    /**
     * Change or set password (authenticated). On success sets passwordChangeSuccess and revalidates;
     * on error sets passwordChangeErrors. Requires current_password if user has a usable password.
     */
    async changePassword(payload: {
      current_password?: string;
      new_password: string;
    }): Promise<void> {
      if (!this.isAuthenticated) return;
      this.passwordChangeLoading = true;
      this.passwordChangeErrors = { fieldErrors: {}, generalError: null };
      this.passwordChangeSuccess = false;
      try {
        const headers = withDjangoLocaleHeaders({
          'Content-Type': 'application/json',
          Accept: 'application/json',
        }) as Record<string, string>;
        if (this.sessionToken) {
          headers['X-Session-Token'] = this.sessionToken;
        }
        const csrfToken = getCookie(CSRF_COOKIE_NAME);
        if (csrfToken) headers['X-CSRFToken'] = csrfToken;

        const body: { current_password?: string; new_password: string } = {
          new_password: payload.new_password,
        };
        if (payload.current_password !== undefined && payload.current_password !== '') {
          body.current_password = payload.current_password;
        }

        const response = await fetch(CHANGE_PASSWORD_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers,
          body: JSON.stringify(body),
        });
        const resBody = (await response.json()) as {
          errors?: Array<{ message?: string; param?: string }>;
        };
        if (response.ok) {
          this.passwordChangeSuccess = true;
          this.passwordChangeErrors = { fieldErrors: {}, generalError: null };
          await this.revalidate();
          return;
        }
        const fieldErrors: Record<string, string[]> = {};
        let generalError: string | null = null;
        const errors = resBody?.errors ?? [{ message: `Request failed (${response.status})` }];
        for (const e of errors) {
          const msg = e?.message ?? 'Invalid value';
          const param = e?.param;
          if (param) {
            if (!fieldErrors[param]) fieldErrors[param] = [];
            fieldErrors[param].push(msg);
          } else {
            generalError = generalError ? `${generalError} ${msg}` : msg;
          }
        }
        this.passwordChangeErrors = { fieldErrors, generalError };
      } catch (e) {
        this.passwordChangeErrors = {
          fieldErrors: {},
          generalError: e instanceof Error ? e.message : 'Something went wrong.',
        };
      } finally {
        this.passwordChangeLoading = false;
      }
    },

    /**
     * Parse API errors array into fieldErrors + generalError (shared pattern for login/signup).
     */
    _setLoginErrorsFromResponse(body: { errors?: Array<{ message?: string; param?: string }> }, status: number): void {
      const fieldErrors: Record<string, string[]> = {};
      const rawList = body?.errors;
      const fallback =
        status === 401 || status === 403
          ? 'Unable to sign in. Check your email and password and try again.'
          : 'Something went wrong. Please try again.';
      const errors =
        Array.isArray(rawList) && rawList.length > 0 ? rawList : [{ message: fallback }];
      let generalError: string | null = null;
      for (const e of errors) {
        const raw = e?.message ?? 'Invalid value';
        const sanitized = sanitizeAuthErrorMessage(raw);
        const param = e?.param;
        const msg =
          sanitized ||
          (param ? raw.trim() || 'Invalid value' : fallback);
        if (param) {
          if (!fieldErrors[param]) fieldErrors[param] = [];
          fieldErrors[param].push(msg);
        } else {
          generalError = generalError ? `${generalError} ${msg}` : msg;
        }
      }
      this.loginErrors = { fieldErrors, generalError };
    },

    /**
     * Sign in with email and password. On success updates session; on error sets loginErrors.
     */
    async login(payload: { email: string; password: string; remember?: boolean }): Promise<void> {
      this.loginLoading = true;
      this.loginErrors = { fieldErrors: {}, generalError: null };
      this.loginCodeConfirmError = null;
      this.loginPendingMfa = null;
      try {
        const headers = withDjangoLocaleHeaders({
          'Content-Type': 'application/json',
          Accept: 'application/json',
        }) as Record<string, string>;
        const csrfToken = getCookie(CSRF_COOKIE_NAME);
        if (csrfToken) headers['X-CSRFToken'] = csrfToken;

        const response = await fetch(LOGIN_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers,
          body: JSON.stringify({
            email: payload.email.trim(),
            password: payload.password,
            remember: payload.remember ?? false,
          }),
        });
        const body = (await response.json()) as {
          data?: { user?: AuthUser; flows?: unknown };
          meta?: { session_token?: string };
          errors?: Array<{ message?: string; param?: string }>;
        };
        if (response.ok) {
          if (body?.meta?.session_token != null) {
            this.setSessionFromResponse({
              user: body.data?.user ?? null,
              session_token: body.meta.session_token,
            });
          } else {
            await this.revalidate();
          }
          this.loginErrors = { fieldErrors: {}, generalError: null };
          this.loginPendingMfa = null;
          return;
        }
        const mfa = parseLoginMfaPending(body);
        if (mfa === 'webauthn' || mfa === 'other') {
          this.loginPendingMfa = mfa;
          this.loginErrors = { fieldErrors: {}, generalError: null };
          return;
        }
        this._setLoginErrorsFromResponse(body, response.status);
      } catch (e) {
        this.loginErrors = {
          fieldErrors: {},
          generalError: e instanceof Error ? e.message : 'Something went wrong.',
        };
      } finally {
        this.loginLoading = false;
      }
    },

    /**
     * GET WebAuthn assertion options and POST credential to the given headless URL.
     * @returns whether the user is now authenticated.
     */
    async _performWebAuthnLogin(webauthnUrl: string): Promise<boolean> {
      const getHeaders = withDjangoLocaleHeaders({ Accept: 'application/json' });
      const getRes = await fetch(webauthnUrl, {
        method: 'GET',
        credentials: 'same-origin',
        headers: getHeaders,
      });
      const getBody = (await getRes.json()) as {
        data?: { request_options?: Record<string, unknown> };
        errors?: Array<{ message?: string; param?: string }>;
      };
      if (!getRes.ok) {
        this._setLoginErrorsFromResponse(
          getBody as { errors?: Array<{ message?: string; param?: string }> },
          getRes.status
        );
        return false;
      }
      const requestOptions = getBody.data?.request_options;
      if (!requestOptions) {
        this.loginErrors = {
          fieldErrors: {},
          generalError: 'Passkey sign-in is not available.',
        };
        return false;
      }
      let credentialJson: Record<string, unknown>;
      try {
        credentialJson = await webAuthnGetAssertion(requestOptions);
      } catch (we) {
        this.loginErrors = {
          fieldErrors: {},
          generalError: webAuthnErrorMessage(we),
        };
        return false;
      }
      const postHeaders = withDjangoLocaleHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }) as Record<string, string>;
      const csrfToken = getCookie(CSRF_COOKIE_NAME);
      if (csrfToken) postHeaders['X-CSRFToken'] = csrfToken;
      const postRes = await fetch(webauthnUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: postHeaders,
        body: JSON.stringify({ credential: credentialJson }),
      });
      const postBody = (await postRes.json()) as {
        data?: { user?: AuthUser };
        meta?: { session_token?: string };
        errors?: Array<{ message?: string; param?: string }>;
      };
      if (postRes.ok) {
        if (postBody?.meta?.session_token != null) {
          this.setSessionFromResponse({
            user: postBody.data?.user ?? null,
            session_token: postBody.meta.session_token,
          });
        } else {
          await this.revalidate();
        }
        this.loginCodeSent = false;
        this.loginCodeSentTo = null;
        this.loginErrors = { fieldErrors: {}, generalError: null };
        return true;
      }
      this._setLoginErrorsFromResponse(postBody, postRes.status);
      return false;
    },

    /**
     * Sign in with a passkey (passwordless WebAuthn). Uses headless GET/POST webauthn/login.
     */
    async loginWithPasskey(): Promise<void> {
      this.loginLoading = true;
      this.loginErrors = { fieldErrors: {}, generalError: null };
      this.loginCodeConfirmError = null;
      try {
        await this._performWebAuthnLogin(PASSKEY_LOGIN_URL);
      } catch (e) {
        this.loginErrors = {
          fieldErrors: {},
          generalError: e instanceof Error ? e.message : 'Something went wrong.',
        };
      } finally {
        this.loginLoading = false;
      }
    },

    /**
     * After email/password returns MFA pending, complete sign-in with WebAuthn (GET/POST webauthn/authenticate).
     */
    async completeLoginWithMfaPasskey(): Promise<void> {
      this.loginLoading = true;
      this.loginErrors = { fieldErrors: {}, generalError: null };
      this.loginCodeConfirmError = null;
      try {
        const ok = await this._performWebAuthnLogin(MFA_WEBAUTHN_AUTHENTICATE_URL);
        if (ok) {
          this.loginPendingMfa = null;
        }
      } catch (e) {
        this.loginErrors = {
          fieldErrors: {},
          generalError: e instanceof Error ? e.message : 'Something went wrong.',
        };
      } finally {
        this.loginLoading = false;
      }
    },

    /**
     * Load WebAuthn passkeys for the current user (authenticated).
     */
    async loadPasskeys(): Promise<void> {
      if (!this.isAuthenticated) {
        this.passkeys = [];
        return;
      }
      try {
        const headers = withDjangoLocaleHeaders({ Accept: 'application/json' }) as Record<
          string,
          string
        >;
        if (this.sessionToken) headers['X-Session-Token'] = this.sessionToken;
        const response = await fetch(AUTHENTICATORS_LIST_URL, {
          method: 'GET',
          credentials: 'same-origin',
          headers,
        });
        const body = (await response.json()) as {
          data?: Array<{ type?: string; id?: number; name?: string }>;
        };
        if (!response.ok || !Array.isArray(body.data)) {
          this.passkeys = [];
          return;
        }
        this.passkeys = body.data
          .filter((a) => a.type === 'webauthn' && typeof a.id === 'number')
          .map((a) => ({ id: a.id as number, name: a.name ?? 'Passkey' }));
      } catch {
        this.passkeys = [];
      }
    },

    /**
     * GET creation options, WebAuthn create, POST credential. Does not set passkeyNeedsReauthenticate.
     */
    async _registerPasskeyCore(options?: { name?: string }): Promise<PasskeyRegistrationCoreResult> {
      const getHeaders = withDjangoLocaleHeaders({ Accept: 'application/json' }) as Record<
        string,
        string
      >;
      if (this.sessionToken) getHeaders['X-Session-Token'] = this.sessionToken;
      const getRes = await fetch(`${WEBAUTHN_MANAGE_URL}?passwordless`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: getHeaders,
      });
      let getBody: unknown;
      try {
        getBody = await getRes.json();
      } catch {
        getBody = {};
      }
      if (!getRes.ok) {
        if (getRes.status === 401 && responseRequiresReauthenticate(getBody)) {
          return { kind: 'reauth' };
        }
        const errGet = getBody as { errors?: Array<{ message?: string }> };
        return {
          kind: 'error',
          message:
            errGet.errors?.[0]?.message ??
            `Could not start registration (${getRes.status}).`,
        };
      }
      const creationOptions = (getBody as { data?: { creation_options?: Record<string, unknown> } })
        .data?.creation_options;
      if (!creationOptions) {
        return { kind: 'error', message: 'Passkey registration is not available.' };
      }
      let credentialJson: Record<string, unknown>;
      try {
        credentialJson = await webAuthnCreateCredential(creationOptions);
      } catch (we) {
        return { kind: 'error', message: webAuthnErrorMessage(we) };
      }
      const postHeaders = withDjangoLocaleHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }) as Record<string, string>;
      if (this.sessionToken) postHeaders['X-Session-Token'] = this.sessionToken;
      const csrfToken = getCookie(CSRF_COOKIE_NAME);
      if (csrfToken) postHeaders['X-CSRFToken'] = csrfToken;
      const trimmedName = options?.name?.trim();
      const postPayload: { credential: Record<string, unknown>; name?: string } = {
        credential: credentialJson,
      };
      if (trimmedName) postPayload.name = trimmedName;
      const postRes = await fetch(WEBAUTHN_MANAGE_URL, {
        method: 'POST',
        credentials: 'same-origin',
        headers: postHeaders,
        body: JSON.stringify(postPayload),
      });
      let postBody: unknown;
      try {
        postBody = await postRes.json();
      } catch {
        postBody = {};
      }
      if (postRes.ok) {
        await this.loadPasskeys();
        return { kind: 'ok' };
      }
      if (postRes.status === 401 && responseRequiresReauthenticate(postBody)) {
        return { kind: 'reauth' };
      }
      const errPost = postBody as { errors?: Array<{ message?: string }> };
      return {
        kind: 'error',
        message:
          errPost.errors?.[0]?.message ?? `Registration failed (${postRes.status}).`,
      };
    },

    /**
     * Confirm account password after idle timeout (django-allauth reauthenticate flow).
     */
    async reauthenticateWithPassword(password: string): Promise<boolean> {
      const headers = withDjangoLocaleHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }) as Record<string, string>;
      if (this.sessionToken) headers['X-Session-Token'] = this.sessionToken;
      const csrfToken = getCookie(CSRF_COOKIE_NAME);
      if (csrfToken) headers['X-CSRFToken'] = csrfToken;
      try {
        const res = await fetch(REAUTHENTICATE_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers,
          body: JSON.stringify({ password }),
        });
        const body = (await res.json().catch(() => ({}))) as {
          errors?: Array<{ message?: string }>;
        };
        if (res.ok) return true;
        const apiMsg = body.errors?.[0]?.message?.trim() ?? '';
        // Headless may use 401 or 400; message is often English from the server.
        const wrongPassword =
          res.status === 401 ||
          (apiMsg.length > 0 && /incorrect\s+password/i.test(apiMsg));
        if (wrongPassword) {
          this.passkeyReauthPasswordErrorI18nKey =
            'account.dashboard.passkeyReauthIncorrectPassword';
          this.passkeyReauthPasswordError = null;
        } else {
          this.passkeyReauthPasswordErrorI18nKey = null;
          this.passkeyReauthPasswordError =
            apiMsg ||
            passkeyT('account.dashboard.passkeyReauthPasswordConfirmFailed', {
              status: res.status,
            });
        }
        return false;
      } catch (e) {
        this.passkeyReauthPasswordErrorI18nKey = null;
        this.passkeyReauthPasswordError =
          e instanceof Error
            ? e.message
            : passkeyT('account.dashboard.passkeyReauthGenericError');
        return false;
      }
    },

    /** Dismiss passkey reauthentication prompt without completing registration. */
    cancelPasskeyReauth(): void {
      this.passkeyNeedsReauthenticate = false;
      this.passkeyPendingRegisterOptions = null;
      this.passkeyReauthPasswordError = null;
      this.passkeyReauthPasswordErrorI18nKey = null;
      this.passkeyReauthCodeLoading = false;
      this.passkeyReauthCodeSent = false;
      this.passkeyReauthCodeSentTo = null;
      this.passkeyReauthCodeError = null;
      this.passkeyRegisterError = null;
      this.passkeyRegisterErrorI18nKey = null;
    },

    /**
     * After successful reauthentication (password or email code), complete stashed passkey registration.
     */
    async _retryPasskeyRegistrationAfterReauth(): Promise<void> {
      const pending = this.passkeyPendingRegisterOptions;
      this.passkeyNeedsReauthenticate = false;
      this.passkeyPendingRegisterOptions = null;
      this.passkeyRegisterError = null;
      this.passkeyRegisterErrorI18nKey = null;
      const result = await this._registerPasskeyCore(
        pending && Object.keys(pending).length ? pending : undefined
      );
      if (result.kind === 'reauth') {
        const t = pending?.name?.trim();
        this.passkeyPendingRegisterOptions = t ? { name: t } : {};
        this.passkeyNeedsReauthenticate = true;
        this.passkeyRegisterErrorI18nKey =
          'account.dashboard.passkeyReauthSessionAgainMessage';
        this.passkeyRegisterError = null;
        return;
      }
      if (result.kind === 'error') {
        this.passkeyRegisterErrorI18nKey = null;
        this.passkeyRegisterError = result.message;
      }
    },

    /**
     * Request a one-time email code to satisfy reauthentication (while adding a passkey).
     */
    async requestPasskeyReauthCode(): Promise<void> {
      if (!this.passkeyNeedsReauthenticate) return;
      this.passkeyReauthCodeError = null;
      this.passkeyReauthCodeLoading = true;
      try {
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        };
        if (this.sessionToken) headers['X-Session-Token'] = this.sessionToken;
        const csrfToken = getCookie(CSRF_COOKIE_NAME);
        if (csrfToken) headers['X-CSRFToken'] = csrfToken;
        const res = await fetch(REAUTHENTICATE_CODE_REQUEST_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers,
          body: JSON.stringify({}),
        });
        const body = (await res.json().catch(() => ({}))) as {
          meta?: { recipient?: string };
          errors?: Array<{ message?: string }>;
        };
        if (res.ok) {
          const to = body.meta?.recipient?.trim();
          this.passkeyReauthCodeSent = true;
          this.passkeyReauthCodeSentTo = to || this.user?.email || null;
          return;
        }
        this.passkeyReauthCodeError =
          body.errors?.[0]?.message ??
          (res.status === 401
            ? passkeyT('account.dashboard.passkeyReauthSignInAgain')
            : passkeyT('account.dashboard.passkeyReauthSendCodeFailed', {
                status: res.status,
              }));
      } catch (e) {
        this.passkeyReauthCodeError =
          e instanceof Error
            ? e.message
            : passkeyT('account.dashboard.passkeyReauthGenericError');
      } finally {
        this.passkeyReauthCodeLoading = false;
      }
    },

    /**
     * Confirm email reauthentication code, then retry passkey registration.
     */
    async confirmPasskeyReauthCodeAndRegister(code: string): Promise<void> {
      if (!this.passkeyNeedsReauthenticate) return;
      this.passkeyReauthCodeError = null;
      this.passkeyRegisterLoading = true;
      try {
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        };
        if (this.sessionToken) headers['X-Session-Token'] = this.sessionToken;
        const csrfToken = getCookie(CSRF_COOKIE_NAME);
        if (csrfToken) headers['X-CSRFToken'] = csrfToken;
        const res = await fetch(REAUTHENTICATE_CODE_CONFIRM_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers,
          body: JSON.stringify({ code: code.trim() }),
        });
        const body = (await res.json().catch(() => ({}))) as {
          data?: { user?: AuthUser };
          meta?: { session_token?: string; is_authenticated?: boolean };
          errors?: Array<{ message?: string; param?: string }>;
        };
        if (!res.ok) {
          this.passkeyReauthCodeError =
            body.errors?.[0]?.message ??
            (res.status === 401
              ? passkeyT('account.dashboard.passkeyReauthSignInAgain')
              : passkeyT('account.dashboard.passkeyReauthVerifyFailed', {
                  status: res.status,
                }));
          return;
        }
        if (body?.meta?.session_token != null) {
          this.setSessionFromResponse({
            user: body.data?.user ?? this.user,
            session_token: body.meta.session_token,
          });
        } else {
          await this.revalidate();
        }
        this.passkeyReauthCodeSent = false;
        this.passkeyReauthCodeSentTo = null;
        await this._retryPasskeyRegistrationAfterReauth();
      } catch (e) {
        this.passkeyReauthCodeError =
          e instanceof Error
            ? e.message
            : passkeyT('account.dashboard.passkeyReauthGenericError');
      } finally {
        this.passkeyRegisterLoading = false;
      }
    },

    /**
     * After successful POST /auth/reauthenticate, retry passkey registration from GET.
     */
    async confirmReauthenticateAndRegisterPasskey(password: string): Promise<void> {
      if (!this.passkeyNeedsReauthenticate) return;
      this.passkeyReauthPasswordError = null;
      this.passkeyReauthPasswordErrorI18nKey = null;
      this.passkeyRegisterLoading = true;
      try {
        const ok = await this.reauthenticateWithPassword(password);
        if (!ok) return;
        await this._retryPasskeyRegistrationAfterReauth();
      } catch (e) {
        this.passkeyRegisterErrorI18nKey = null;
        this.passkeyRegisterError =
          e instanceof Error
            ? e.message
            : passkeyT('account.dashboard.passkeyReauthGenericError');
      } finally {
        this.passkeyRegisterLoading = false;
      }
    },

    /**
     * Register a passwordless passkey for the signed-in user.
     * Optional `name` is shown in the passkey list; omit or leave empty for server default.
     * If the server returns 401 with `flows` containing `reauthenticate`, sets passkeyNeedsReauthenticate.
     */
    async registerPasskey(options?: { name?: string }): Promise<void> {
      if (!this.isAuthenticated) return;
      this.passkeyRegisterLoading = true;
      this.passkeyRegisterError = null;
      this.passkeyRegisterErrorI18nKey = null;
      this.passkeyNeedsReauthenticate = false;
      this.passkeyReauthPasswordError = null;
      this.passkeyReauthPasswordErrorI18nKey = null;
      this.passkeyReauthCodeSent = false;
      this.passkeyReauthCodeSentTo = null;
      this.passkeyReauthCodeError = null;
      this.passkeyPendingRegisterOptions = null;
      try {
        const result = await this._registerPasskeyCore(options);
        if (result.kind === 'reauth') {
          const t = options?.name?.trim();
          this.passkeyPendingRegisterOptions = t ? { name: t } : {};
          this.passkeyNeedsReauthenticate = true;
          this.passkeyRegisterErrorI18nKey =
            'account.dashboard.passkeyReauthSecurityMessage';
          this.passkeyRegisterError = null;
          return;
        }
        if (result.kind === 'error') {
          this.passkeyRegisterErrorI18nKey = null;
          this.passkeyRegisterError = result.message;
        }
      } catch (e) {
        this.passkeyRegisterErrorI18nKey = null;
        this.passkeyRegisterError =
          e instanceof Error
            ? e.message
            : passkeyT('account.dashboard.passkeyReauthGenericError');
      } finally {
        this.passkeyRegisterLoading = false;
      }
    },

    /**
     * Rename a WebAuthn passkey (authenticated). PUT headless webauthn manage URL.
     */
    async renamePasskey(authenticatorId: number, name: string): Promise<void> {
      if (!this.isAuthenticated) return;
      this.passkeyRegisterError = null;
      this.passkeyRegisterErrorI18nKey = null;
      const trimmed = name.trim();
      if (!trimmed) {
        this.passkeyRegisterError = 'Enter a name for this passkey.';
        return;
      }
      this.passkeyRenameLoading = true;
      try {
        const headers = withDjangoLocaleHeaders({
          'Content-Type': 'application/json',
          Accept: 'application/json',
        }) as Record<string, string>;
        if (this.sessionToken) headers['X-Session-Token'] = this.sessionToken;
        const csrfToken = getCookie(CSRF_COOKIE_NAME);
        if (csrfToken) headers['X-CSRFToken'] = csrfToken;
        const response = await fetch(WEBAUTHN_MANAGE_URL, {
          method: 'PUT',
          credentials: 'same-origin',
          headers,
          body: JSON.stringify({ id: authenticatorId, name: trimmed }),
        });
        if (response.ok) {
          await this.loadPasskeys();
          return;
        }
        const body = (await response.json()) as {
          errors?: Array<{ message?: string }>;
        };
        this.passkeyRegisterError =
          body.errors?.[0]?.message ?? `Could not rename passkey (${response.status}).`;
      } catch (e) {
        this.passkeyRegisterError =
          e instanceof Error ? e.message : 'Something went wrong.';
      } finally {
        this.passkeyRenameLoading = false;
      }
    },

    /**
     * Remove a passkey by authenticator id (authenticated).
     */
    async removePasskey(authenticatorId: number): Promise<void> {
      if (!this.isAuthenticated) return;
      this.passkeyRegisterError = null;
      this.passkeyRegisterErrorI18nKey = null;
      try {
        const headers = withDjangoLocaleHeaders({
          'Content-Type': 'application/json',
          Accept: 'application/json',
        }) as Record<string, string>;
        if (this.sessionToken) headers['X-Session-Token'] = this.sessionToken;
        const csrfToken = getCookie(CSRF_COOKIE_NAME);
        if (csrfToken) headers['X-CSRFToken'] = csrfToken;
        const response = await fetch(WEBAUTHN_MANAGE_URL, {
          method: 'DELETE',
          credentials: 'same-origin',
          headers,
          body: JSON.stringify({ authenticators: [authenticatorId] }),
        });
        if (response.ok) {
          await this.loadPasskeys();
          return;
        }
        const body = (await response.json()) as {
          errors?: Array<{ message?: string }>;
        };
        this.passkeyRegisterError =
          body.errors?.[0]?.message ?? `Could not remove passkey (${response.status}).`;
      } catch (e) {
        this.passkeyRegisterError =
          e instanceof Error ? e.message : 'Something went wrong.';
      }
    },

    /**
     * Request a login code sent by email. On success sets loginCodeSent; on error sets loginErrors.
     */
    async requestLoginCode(email: string): Promise<void> {
      this.loginLoading = true;
      this.loginErrors = { fieldErrors: {}, generalError: null };
      this.loginCodeConfirmError = null;
      try {
        const headers = withDjangoLocaleHeaders({
          'Content-Type': 'application/json',
          Accept: 'application/json',
        }) as Record<string, string>;
        const csrfToken = getCookie(CSRF_COOKIE_NAME);
        if (csrfToken) headers['X-CSRFToken'] = csrfToken;

        const response = await fetch(LOGIN_CODE_REQUEST_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers,
          body: JSON.stringify({ email: email.trim() }),
        });
        const body = (await response.json()) as {
          errors?: Array<{ message?: string; param?: string }>;
        };
        // Headless API returns 401 when not authenticated even when the request succeeded (email sent).
        const success =
          response.ok || (response.status === 401 && !(body?.errors?.length ?? 0));
        if (success) {
          this.loginCodeSent = true;
          this.loginCodeSentTo = email.trim();
          this.loginErrors = { fieldErrors: {}, generalError: null };
          return;
        }
        this._setLoginErrorsFromResponse(
          body as { errors?: Array<{ message?: string; param?: string }> },
          response.status
        );
      } catch (e) {
        this.loginErrors = {
          fieldErrors: {},
          generalError: e instanceof Error ? e.message : 'Something went wrong.',
        };
      } finally {
        this.loginLoading = false;
      }
    },

    /**
     * Confirm login code (from email). On success updates session and clears code state; on error sets loginCodeConfirmError.
     */
    async confirmLoginCode(code: string): Promise<void> {
      this.loginLoading = true;
      this.loginCodeConfirmError = null;
      try {
        const headers = withDjangoLocaleHeaders({
          'Content-Type': 'application/json',
          Accept: 'application/json',
        }) as Record<string, string>;
        const csrfToken = getCookie(CSRF_COOKIE_NAME);
        if (csrfToken) headers['X-CSRFToken'] = csrfToken;

        const response = await fetch(LOGIN_CODE_CONFIRM_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers,
          body: JSON.stringify({ code: code.trim() }),
        });
        const body = (await response.json()) as {
          data?: { user?: AuthUser };
          meta?: { session_token?: string; is_authenticated?: boolean };
          errors?: Array<{ message?: string }>;
        };
        const authenticated =
          response.ok &&
          (body?.meta?.is_authenticated === true || body?.data?.user != null);
        if (authenticated) {
          if (body?.meta?.session_token != null) {
            this.setSessionFromResponse({
              user: body.data?.user ?? null,
              session_token: body.meta.session_token,
            });
          } else {
            await this.revalidate();
          }
          this.loginCodeSent = false;
          this.loginCodeSentTo = null;
          this.loginCodeConfirmError = null;
          this.loginErrors = { fieldErrors: {}, generalError: null };
          return;
        }
        const mfa = parseLoginMfaPending(body);
        if (mfa === 'webauthn' || mfa === 'other') {
          this.loginPendingMfa = mfa;
          this.loginCodeConfirmError = null;
          this.loginErrors = { fieldErrors: {}, generalError: null };
          return;
        }
        const loginCodeFallback =
          'The code is invalid or has expired. Try requesting a new login link.';
        const raw = body?.errors?.[0]?.message ?? loginCodeFallback;
        this.loginCodeConfirmError = sanitizeAuthErrorMessage(raw) || loginCodeFallback;
      } catch (e) {
        this.loginCodeConfirmError =
          e instanceof Error ? e.message : 'Something went wrong.';
      } finally {
        this.loginLoading = false;
      }
    },

    /**
     * Submit signup to the headless API. On success sets signupSuccess and revalidates
     * session; on error sets signupErrors. Uses browser client so session cookie is set.
     * Optional `next` is sent so the verification email link can redirect there after confirm.
     */
    async signup(payload: {
      email: string;
      first_name: string;
      last_name: string;
      phone?: string;
      introduction?: string;
      next?: string;
    }): Promise<void> {
      this.signupLoading = true;
      this.signupErrors = { fieldErrors: {}, generalError: null };
      this.signupSuccess = false;
      try {
        const headers = withDjangoLocaleHeaders({
          'Content-Type': 'application/json',
          Accept: 'application/json',
        }) as Record<string, string>;
        const csrfToken = getCookie(CSRF_COOKIE_NAME);
        if (csrfToken) headers['X-CSRFToken'] = csrfToken;

        const bodyPayload: Record<string, string> = {
          email: payload.email.trim(),
          first_name: payload.first_name.trim(),
          last_name: payload.last_name.trim(),
        };
        if (payload.phone !== undefined && payload.phone.trim() !== '') {
          bodyPayload.phone = payload.phone.trim();
        }
        if (payload.introduction !== undefined && payload.introduction.trim() !== '') {
          bodyPayload.introduction = payload.introduction.trim();
        }
        if (
          payload.next !== undefined &&
          typeof payload.next === 'string' &&
          payload.next.startsWith('/') &&
          !payload.next.includes('//')
        ) {
          bodyPayload.next = payload.next;
        }

        const response = await fetch(SIGNUP_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers,
          body: JSON.stringify(bodyPayload),
        });
        const body = (await response.json()) as {
          status?: number;
          data?: {
            user?: unknown;
            flows?: Array<{ id: string; is_pending?: boolean }>;
          };
          meta?: { is_authenticated?: boolean };
          errors?: Array<{ message?: string; param?: string }>;
        };
        const flows = body?.data?.flows ?? [];
        const hasPendingVerifyEmail = flows.some(
          (f) => f.id === 'verify_email' && f.is_pending === true
        );
        if (response.ok || (response.status === 401 && hasPendingVerifyEmail)) {
          this.signupSuccess = true;
          await this.revalidate();
          return;
        }
        if (!response.ok) {
          const fieldErrors: Record<string, string[]> = {};
          const errors = body?.errors ?? [{ message: `Request failed (${response.status})` }];
          let generalError: string | null = null;
          for (const e of errors) {
            const msg = e?.message ?? 'Invalid value';
            const param = e?.param;
            if (param) {
              if (!fieldErrors[param]) fieldErrors[param] = [];
              fieldErrors[param].push(msg);
            } else {
              generalError = generalError ? `${generalError} ${msg}` : msg;
            }
          }
          this.signupErrors = { fieldErrors, generalError };
          return;
        }
      } catch (e) {
        this.signupErrors = {
          fieldErrors: {},
          generalError: e instanceof Error ? e.message : 'Something went wrong.',
        };
      } finally {
        this.signupLoading = false;
      }
    },

    /**
     * Confirm email with the headless API (link from verification email).
     * POSTs the key to the verify-email endpoint. On success revalidates session.
     * Returns { ok: true } or { ok: false, error: string }.
     */
    async verifyEmail(key: string): Promise<
      | { ok: true }
      | { ok: false; error: string; code?: string }
    > {
      const headers = withDjangoLocaleHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }) as Record<string, string>;
      const csrfToken = getCookie(CSRF_COOKIE_NAME);
      if (csrfToken) headers['X-CSRFToken'] = csrfToken;
      try {
        const response = await fetch(VERIFY_EMAIL_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers,
          body: JSON.stringify({ key: key.trim() }),
        });
        const body = (await response.json().catch(() => ({}))) as {
          errors?: Array<{ message?: string; code?: string }>;
        };
        if (response.ok) {
          await this.revalidate();
          return { ok: true };
        }
        const verifyFallback = 'This verification link is invalid or has expired.';
        const firstError = body?.errors?.[0];
        const raw = firstError?.message ?? verifyFallback;
        const msg = sanitizeAuthErrorMessage(raw) || verifyFallback;
        return { ok: false, error: msg, code: firstError?.code };
      } catch (e) {
        return {
          ok: false,
          error: sanitizeAuthErrorMessage(
            e instanceof Error ? e.message : 'Something went wrong.'
          ) || 'Something went wrong.',
        };
      }
    },

    /**
     * Resend the email-address confirmation message (same session as signup / pending verify).
     */
    async resendEmailVerification(): Promise<
      | { ok: true }
      | { ok: false; code: 'no_pending' | 'already_verified' | 'network'; message?: string }
    > {
      const headers = withDjangoLocaleHeaders({
        Accept: 'application/json',
      }) as Record<string, string>;
      const csrfToken = getCookie(CSRF_COOKIE_NAME);
      if (csrfToken) headers['X-CSRFToken'] = csrfToken;
      try {
        const response = await fetch(RESEND_VERIFY_EMAIL_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers,
        });
        const body = (await response.json().catch(() => ({}))) as {
          ok?: boolean;
          code?: string;
        };
        if (response.ok && body?.ok === true) {
          return { ok: true };
        }
        if (body?.code === 'no_pending') {
          return { ok: false, code: 'no_pending' };
        }
        if (body?.code === 'already_verified') {
          return { ok: false, code: 'already_verified' };
        }
        return { ok: false, code: 'network' };
      } catch {
        return { ok: false, code: 'network' };
      }
    },

    /**
     * Request a password reset email (headless POST …/auth/password/request).
     * The server may hide whether the address exists (enumeration protection).
     */
    async requestPasswordReset(
      email: string
    ): Promise<{ ok: true } | { ok: false; error: string }> {
      const headers = withDjangoLocaleHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }) as Record<string, string>;
      const csrfToken = getCookie(CSRF_COOKIE_NAME);
      if (csrfToken) headers['X-CSRFToken'] = csrfToken;
      try {
        const response = await fetch(PASSWORD_RESET_REQUEST_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers,
          body: JSON.stringify({ email: email.trim().toLowerCase() }),
        });
        const body = (await response.json().catch(() => ({}))) as {
          errors?: Array<{ message?: string }>;
        };
        if (response.ok) {
          return { ok: true };
        }
        const msg =
          body?.errors?.map((e) => e?.message).filter(Boolean).join(' ') ||
          `Request failed (${response.status}).`;
        return { ok: false, error: msg };
      } catch (e) {
        return {
          ok: false,
          error: e instanceof Error ? e.message : 'Something went wrong.',
        };
      }
    },

    /**
     * Validate reset token (headless GET …/auth/password/reset with X-Password-Reset-Key).
     */
    async fetchPasswordResetKey(
      key: string
    ): Promise<{ ok: true; user: AuthUser | null } | { ok: false; error: string }> {
      const k = key.trim();
      if (!k) {
        return { ok: false, error: 'Invalid reset link.' };
      }
      const headers = withDjangoLocaleHeaders({
        Accept: 'application/json',
        'X-Password-Reset-Key': k,
      }) as Record<string, string>;
      try {
        const response = await fetch(PASSWORD_RESET_URL, {
          method: 'GET',
          credentials: 'same-origin',
          headers,
        });
        const body = (await response.json().catch(() => ({}))) as {
          data?: { user?: AuthUser };
          errors?: Array<{ message?: string }>;
        };
        if (response.ok) {
          return { ok: true, user: body.data?.user ?? null };
        }
        const msg =
          body?.errors?.map((e) => e?.message).filter(Boolean).join(' ') ||
          `This reset link is invalid or has expired (${response.status}).`;
        return { ok: false, error: msg };
      } catch (e) {
        return {
          ok: false,
          error: e instanceof Error ? e.message : 'Something went wrong.',
        };
      }
    },

    /**
     * Set a new password using the token from the email (headless POST …/auth/password/reset).
     */
    async completePasswordReset(payload: {
      key: string;
      password: string;
    }): Promise<{ ok: true } | { ok: false; error: string }> {
      const headers = withDjangoLocaleHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }) as Record<string, string>;
      const csrfToken = getCookie(CSRF_COOKIE_NAME);
      if (csrfToken) headers['X-CSRFToken'] = csrfToken;
      try {
        const response = await fetch(PASSWORD_RESET_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers,
          body: JSON.stringify({
            key: payload.key.trim(),
            password: payload.password,
          }),
        });
        const body = (await response.json().catch(() => ({}))) as {
          errors?: Array<{ message?: string }>;
        };
        if (response.ok) {
          await this.revalidate();
          return { ok: true };
        }
        // When ACCOUNT_LOGIN_ON_PASSWORD_RESET is False, allauth still resets the password but
        // returns 401 because AuthenticationResponse uses anonymous request.user. Treat 401
        // without input errors as success: refresh session (if auto-login enabled) or send
        // the user to sign in without a false failure message.
        const hasErrors = Array.isArray(body.errors) && body.errors.length > 0;
        if (response.status === 401 && !hasErrors) {
          await this.initialize();
          return { ok: true };
        }
        const msg =
          body?.errors?.map((e) => e?.message).filter(Boolean).join(' ') ||
          `Reset failed (${response.status}).`;
        return { ok: false, error: msg };
      } catch (e) {
        return {
          ok: false,
          error: e instanceof Error ? e.message : 'Something went wrong.',
        };
      }
    },
  },
});
